if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get variants() {
      if (!this._variants) {
        const variantsData = this.querySelector('[data-product-variants]');
        this._variants = variantsData ? JSON.parse(variantsData.textContent) : [];
      }
      return this._variants;
    }

    get variantMetafields() {
      if (!this._variantMetafields) {
        const metafieldsData = this.querySelector('[data-variant-metafields]');
        this._variantMetafields = metafieldsData ? JSON.parse(metafieldsData.textContent) : [];
      }
      return this._variantMetafields;
    }

    get optionGroups() {
      return Array.from(this.querySelectorAll('.m-product-option__inner'));
    }

    getOptionControls(group) {
      return Array.from(group.querySelectorAll('select, input[type="radio"]'));
    }

    getControlValue(group) {
      const control = group.querySelector('select') || group.querySelector('input[type="radio"]:checked');
      return control ? control.value : '';
    }

    setGroupValue(group, value) {
      const controls = this.getOptionControls(group);
      const selectedControl = controls.find((control) => control.value === value);
      if (!selectedControl) return;

      if (selectedControl.tagName.toLowerCase() === 'select') {
        selectedControl.value = value;
      } else {
        selectedControl.checked = true;
      }

      const selectedLabel = group.querySelector('.option-label--selected');
      if (selectedLabel) selectedLabel.textContent = value;
      group.dataset.selectedValue = value;
    }

    setVariant(variant) {
      if (!variant?.options) return;

      this.optionGroups.forEach((group, groupIndex) => {
        this.setGroupValue(group, variant.options[groupIndex]);
      });
    }

    getBestellmengeGroupIndex() {
      return this.optionGroups.findIndex((group) => {
        const optionName = group.dataset.optionName || '';
        return optionName.trim().toLowerCase() === 'bestellmenge';
      });
    }

    getSelectedValues() {
      return this.optionGroups.map((group) => {
        const value = this.getControlValue(group);
        if (value) return value;

        return this.getOptionControls(group).find((control) => {
          return !control.disabled;
        })?.value || '';
      });
    }

    getFirstValidBestellmengeVariant() {
      const quantityIndex = this.getBestellmengeGroupIndex();
      if (quantityIndex === -1) return null;

      const selectedValues = this.getSelectedValues();
      const quantityControls = this.getOptionControls(this.optionGroups[quantityIndex]);
      const availableVariants = this.variants.filter((variant) => variant.available);

      for (const control of quantityControls) {
        if (control.disabled) continue;

        const variant = availableVariants.find((candidate) => {
          return candidate.options.every((value, index) => {
            return index === quantityIndex || !selectedValues[index] || value === selectedValues[index];
          }) && candidate.options[quantityIndex] === control.value;
        });

        if (variant) return variant;
      }

      return availableVariants.find((candidate) => {
        return candidate.options.every((value, index) => {
          return index === quantityIndex || !selectedValues[index] || value === selectedValues[index];
        });
      }) || null;
    }

    initializeBestellmengeVariant() {
      const variant = this.getFirstValidBestellmengeVariant();
      if (variant) this.setVariant(variant);
      return variant;
    }

    getBestellmengeControlForVariant(variant) {
      const quantityIndex = this.getBestellmengeGroupIndex();
      if (quantityIndex === -1 || !variant?.options) return null;

      return this.getOptionControls(this.optionGroups[quantityIndex]).find((control) => {
        return control.value === variant.options[quantityIndex];
      }) || null;
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    get selectedVariant() {
      const selectedValues = this.optionGroups.map((group) => this.getControlValue(group));
      if (selectedValues.some((value) => !value)) return null;

      return this.variants.find((variant) => {
        return variant.options.every((value, index) => value === selectedValues[index]);
      }) || null;
    }

    connectedCallback() {
      this.addEventListener('change', (event) => {
        const target = this.getInputForEventTarget(event.target);
        MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, {
          data: {
            selectedOptionValues: this.selectedOptionValues,
            selectedVariant: this.selectedVariant,
            event,
            target
          }
        });
      });
    }

    getInputForEventTarget(target) {
      return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }
  }
  customElements.define("variant-picker", VariantPicker);
}

// if (!customElements.get("variant-picker")) {
//   class VariantPicker extends HTMLElement {
//     constructor() {
//       super();
//     }

//     get selectedOptionValues() {
//       const options = this.querySelectorAll('.m-product-option select, .m-product-option input');
//       const selectedValues = new Map();

//       options.forEach((field) => {
//         const fieldName = field.name || field.dataset.fieldName;

//         if (field.tagName.toLowerCase() === "select") {
//           const selectedOption = Array.from(field.options).find(
//             (opt) => opt.selected && !opt.disabled
//           );
//           if (selectedOption) {
//             selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
//           }
//         } else if (
//           field.tagName.toLowerCase() === "input" &&
//           field.type === "radio"
//         ) {
//           const checkedInput = field.checked ? field : null;
//           if (checkedInput) {
//             selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
//           }
//         }
//       });

//       options.forEach((field) => {
//         const fieldName = field.name || field.dataset.fieldName;

//         if (!selectedValues.has(fieldName)) {
//           if (field.tagName.toLowerCase() === "select") {
//             const firstValidOption = Array.from(field.options).find(
//               (opt) => !opt.disabled
//             );
//             if (firstValidOption) {
//               selectedValues.set(
//                 fieldName,
//                 firstValidOption.dataset.optionValueId
//               );
//             }
//           } else if (
//             field.tagName.toLowerCase() === "input" &&
//             field.type === "radio"
//           ) {
//             const firstInput = field;
//             if (firstInput) {
//               selectedValues.set(fieldName, firstInput.dataset.optionValueId);
//             }
//           }
//         }
//       });

//       const result = [];
//       options.forEach((field) => {
//         const fieldName = field.name || field.dataset.fieldName;
//         if (selectedValues.has(fieldName)) {
//           const optionValueId = selectedValues.get(fieldName);
//           if (optionValueId && !result.includes(optionValueId)) {
//             result.push(optionValueId);
//           }
//         }
//       });
//       return result;
//     }

//     connectedCallback() {
//       this.addEventListener('change', (event) => {
//         const target = this.getInputForEventTarget(event.target);
//         MinimogEvents.emit(MinimogTheme.pubSubEvents.optionValueSelectionChange, { data: { selectedOptionValues: this.selectedOptionValues, event, target } });
//       });
//     }

//     getInputForEventTarget(target) {
//       return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
//     }
//   }
//   customElements.define("variant-picker", VariantPicker);
// }